/**
 * useCancelChargeManual - Hook para cancelar uma cobrança/fatura
 *
 * Fluxo (v3.0.0 — Fonte Única RPC + q_charge):
 * 1. Chama a RPC `cancel_charge_manual` (única porta).
 *    A RPC:
 *      - Valida tenant ownership da cobrança
 *      - Cancela cobrança + parcelas localmente (atômico)
 *      - Registra evento `charge_cancelled_manual` na timeline
 *      - Enfileira `cancel_charge` em q_charge para cada parcela com
 *        provider_payment_id (parcelada) ou 1 mensagem (avulsa) — o
 *        financial-charge-worker sincroniza com o provider em segundo
 *        plano. Sucesso/erro vira evento na timeline.
 * 2. Invalida caches.
 *
 * Removido em v3.0.0:
 *   - chamada direta à edge function `financial-charge` (action cancel_charge)
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-09 v1.0.0 - Criação do hook
 * RELEASE: 2026-06-16 v2.0.0 - Edge function resolve provider_charge_id server-side
 * RELEASE: 2026-06-17 v3.0.0 - FONTE ÚNICA: RPC enfileira q_charge internamente.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useCancelChargeManual');

export interface CancelChargePayload {
  cobranca_id: string;
  reason?: string | null;
  /** @deprecated v3.0.0 — RPC resolve do banco. Mantido para retrocompat de UI. */
  provider_charge_id?: string | null;
  /** @deprecated v3.0.0 — RPC resolve do banco. */
  conta_corrente_id?: string | null;
}

export function useCancelChargeManual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CancelChargePayload) => {
      logger.debug('Cancelando cobrança via RPC única', { cobranca_id: payload.cobranca_id });

      const { data, error } = await supabase.rpc('cancel_charge_manual', {
        p_cobranca_id: payload.cobranca_id,
        p_reason: payload.reason || null,
      });

      if (error) {
        logger.error('Erro ao chamar RPC cancel_charge_manual', { error: error.message });
        throw error;
      }

      return data as {
        success: boolean;
        parcelas_canceladas: number;
        sync_enqueued?: number;
      };
    },
    onSuccess: (data, variables) => {
      const enqueued = (data as { sync_enqueued?: number })?.sync_enqueued ?? 0;
      if (enqueued > 0) {
        toast.success('Cobrança cancelada. Sincronização com o provedor em andamento.');
      } else {
        toast.success('Cobrança cancelada com sucesso');
      }
      queryClient.invalidateQueries({ queryKey: ['cobranca-details', variables.cobranca_id] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-parcelas'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao cancelar cobrança');
    },
  });
}
